/** Published contractor offerings. Prices are authored by the company, never seeded. */
import { OPERATOR_GENERAL_WORK_JOB_TYPES } from './operatorDeskGeneralWork';
export const CONTRACTOR_SERVICES = [
  { id: 'water', label: 'Water', group: 'Service flows' },
  { id: 'grocery', label: 'Grocery', group: 'Service flows' },
  { id: 'hotshots', label: 'Hotshot', group: 'Service flows' },
  { id: 'waste_management', label: 'Waste management', group: 'Service flows' },
  { id: 'tacos', label: 'Tacos / food delivery', group: 'Service flows' },
  { id: 'welding', label: 'Welding', group: 'Service flows' },
  { id: 'porta_potty', label: 'Porta', group: 'Service flows' },
  { id: 'semis', label: 'Semi', group: 'Service flows' },
  ...OPERATOR_GENERAL_WORK_JOB_TYPES,
] as const;
export const SERVICE_PRICE_UNITS = ['job', 'hour', 'day', 'week', 'unit', 'load', 'barrel', 'foot', 'mile', 'pallet'] as const;
export type ServicePriceUnit = typeof SERVICE_PRICE_UNITS[number];
export type ServiceProduct = { id: string; name: string; unit: ServicePriceUnit; priceUsd: number | null };
export type ContractorServiceOffering = { service: string; products: ServiceProduct[] };
export type ServiceSelection = { productId: string; quantity: number };
export type BookedServiceQuote = {
  items: { name: string; unit: ServicePriceUnit; quantity: number; unitPriceCents: number | null; lineTotalCents: number | null }[];
  totalCents: number | null;
};
const ids = new Set<string>(CONTRACTOR_SERVICES.map(s => s.id));
export const contractorServiceLabel = (id: string) => CONTRACTOR_SERVICES.find(s => s.id === id)?.label || id;
export const RATE_ONLY_SERVICES = new Set(['grocery', 'hotshots', 'tacos', 'welding', 'semis']);
export const FIXED_PRODUCT_SERVICES = new Set(['water', 'waste_management', 'porta_potty']);
const templates: Record<string, [string, string, ServicePriceUnit][]> = {
  water: [
    ['kirkland', 'Kirkland Signature water pallet', 'pallet'],
    ['members_mark', "Member's Mark water pallet", 'pallet'],
    ['generic', 'Generic / Waterpallets purified pallet', 'pallet'],
    ['nestle', 'Nestlé Pure Life water pallet', 'pallet'],
    ['deer_park', 'Deer Park water pallet', 'pallet'],
    ['arrowhead', 'Arrowhead', 'pallet'],
    ['ozarka', 'Ozarka', 'pallet'],
    ['aquafina', 'Aquafina', 'pallet'],
    ['dasani', 'Dasani', 'pallet'],
    ['smartwater', 'smartwater', 'pallet'],
    ['essentia', 'Essentia', 'pallet'],
    ['poland_spring', 'Poland Spring', 'pallet'],
    ['ice_mountain', 'Ice Mountain', 'pallet'],
    ['zephyrhills', 'Zephyrhills', 'pallet'],
    ['crystal_geyser', 'Crystal Geyser water pallet', 'pallet'],
    ['niagara', 'Niagara water pallet', 'pallet'],
    ['roxane', 'Roxane purified water pallet', 'pallet'],
    ['fiji', 'FIJI water pallet', 'pallet'],
    ['evian', 'evian water pallet', 'pallet'],
    ['core', 'CORE Hydration pallet', 'pallet'],
    ['lifewtr', 'LIFEWTR pallet', 'pallet'],
    ['alkaline88', 'Alkaline88 water pallet', 'pallet'],
    ['great_value', 'Great Value water pallet', 'pallet'],
    ['market_pantry', 'Store-brand water pallet', 'pallet'],
    ['5gal_generic', '5-gal jug pallet', 'pallet'],
    ['custom', 'Other water brand…', 'pallet'],
    ['gatorade', 'Gatorade Thirst Quencher pallet', 'pallet'],
    ['gatorade_zero', 'Gatorade Zero pallet', 'pallet'],
    ['gatorade_g2', 'Gatorade G2 pallet', 'pallet'],
    ['bodyarmor', 'BODYARMOR sports pallet', 'pallet'],
    ['liquid_iv', 'Liquid I.V. pallet', 'pallet'],
    ['liquid_death', 'Liquid Death can pallet', 'pallet'],
    ['sports_custom', 'Other sports brand…', 'pallet'],
    ['pepsi', 'Pepsi can pallet', 'pallet'],
    ['tropicana', 'Tropicana juice pallet', 'pallet'],
    ['frito_lay', 'Frito-Lay snack pallet', 'pallet'],
    ['quaker', 'Quaker breakfast pallet', 'pallet'],
  ],
  waste_management: [
    ...['10ft','12ft','15ft','20ft','25ft','30ft','40ft','50ft'].map(size =>
      [`${size}_week`, `${size.replace('ft', '')} yd dumpster package`, 'week'] as [string, string, ServicePriceUnit]),
    ['additional_day', 'Additional rental day', 'day'],
  ],
};
const oilfieldOptions: Record<string, [string, string, ServicePriceUnit]> = {
  pumper: ['Lease rounds', 'Production reporting', 'day'],
  well_check: ['Well inspection', 'Tank gauging', 'unit'],
  roustabout: ['Roustabout crew', 'Equipment support', 'hour'],
  lease_maintenance: ['Lease repairs', 'Preventive maintenance', 'job'],
  maintenance: ['Equipment repair', 'Preventive maintenance', 'job'],
  chemical_treating: ['Chemical delivery', 'Injection service', 'job'],
  hot_oiling: ['Hot oil truck', 'Circulation service', 'hour'],
  hot_watering: ['Hot water truck', 'Circulation service', 'hour'],
  paraffin_cutting: ['Paraffin cutting', 'Tubing scraping', 'job'],
  automation_scada: ['SCADA troubleshooting', 'Controls installation', 'hour'],
  measurement: ['Meter proving', 'Meter calibration', 'unit'],
  lact_service: ['LACT inspection', 'LACT repair', 'job'],
  workover: ['Workover rig and crew', 'Mobilization', 'day'],
  pulling_unit: ['Rod job', 'Tubing job', 'job'],
  swabbing: ['Swab unit and crew', 'Mobilization', 'hour'],
  coiled_tubing: ['Coiled tubing unit', 'Crew support', 'hour'],
  nitrogen_services: ['Nitrogen pumping', 'Nitrogen transport', 'job'],
  slickline: ['Slickline unit and crew', 'Tool package', 'day'],
  wireline: ['Wireline unit and crew', 'Tool package', 'day'],
  logging: ['Logging run', 'Logging equipment', 'job'],
  acidizing: ['Acid pumping', 'Fluid supply', 'job'],
  cementing: ['Cement pumping', 'Cement supply', 'job'],
  fishing: ['Fishing crew', 'Fishing tools', 'day'],
  snubbing: ['Snubbing unit', 'Support crew', 'day'],
  well_testing: ['Test equipment', 'Test crew', 'day'],
  completion: ['Completion crew', 'Completion equipment', 'day'],
  frac_support: ['Frac support crew', 'Support equipment', 'day'],
  flowback: ['Flowback crew', 'Flowback equipment', 'day'],
  sand_hauling: ['Proppant delivery', 'Standby time', 'load'],
  frac_water_transfer: ['Transfer pumps', 'Transfer lines', 'day'],
  rig_move: ['Rig transport', 'Move support crew', 'job'],
  rig_up_rig_down: ['Rig-up crew', 'Rig-down crew', 'job'],
  pumping_unit_service: ['Pumping unit installation', 'Pumping unit repair', 'job'],
  rod_pump_repair: ['Pump inspection', 'Pump repair', 'unit'],
  esp_service: ['ESP installation', 'ESP troubleshooting', 'job'],
  gas_lift: ['Gas lift installation', 'Gas lift service', 'job'],
  plunger_lift: ['Plunger installation', 'Plunger service', 'job'],
  produced_water_hauling: ['Produced water transport', 'Truck standby', 'load'],
  crude_hauling: ['Crude transport', 'Truck standby', 'load'],
  vacuum_truck: ['Vacuum truck and driver', 'Tank evacuation', 'hour'],
  kill_truck: ['Fluid transport', 'Kill truck service', 'load'],
  frac_tank_rental: ['Frac tank rental', 'Tank delivery', 'day'],
  swd_disposal: ['Disposal volume', 'Fluid handling', 'barrel'],
  construction: ['Construction crew', 'Construction equipment', 'day'],
  tank_battery: ['Tank battery installation', 'Tank battery repair', 'job'],
  tank_cleaning: ['Tank cleaning crew', 'Waste removal', 'job'],
  separator_service: ['Separator service', 'Heater treater service', 'job'],
  compressor_service: ['Compressor maintenance', 'Compressor repair', 'job'],
  pipeline_construction: ['Pipeline installation', 'Pipeline tie-in', 'job'],
  pipeline_repair: ['Leak repair', 'Pipe replacement', 'job'],
  pigging: ['Pigging run', 'Pigging equipment', 'job'],
  hydrotesting: ['Hydrotest crew', 'Test equipment', 'day'],
  cathodic_protection: ['Cathodic survey', 'Cathodic system repair', 'job'],
  flowline_service: ['Flowline installation', 'Flowline repair', 'job'],
  valve_wellhead_service: ['Valve service', 'Wellhead service', 'job'],
  fabrication: ['Shop fabrication', 'On-site fabrication', 'hour'],
  electrical: ['Electrical installation', 'Electrical troubleshooting', 'hour'],
  instrumentation: ['Instrument calibration', 'Instrument installation', 'unit'],
  lease_road_maintenance: ['Road grading', 'Road repairs', 'job'],
  pad_construction: ['Pad preparation', 'Pad construction', 'job'],
  dirt_work: ['Dozer and operator', 'Grading', 'hour'],
  caliche_hauling: ['Caliche delivery', 'Spreading', 'load'],
  hydro_excavation: ['Hydro-excavation truck', 'Spoils removal', 'hour'],
  backhoe: ['Backhoe and operator', 'Excavator and operator', 'hour'],
  mowing_weed_control: ['Mowing', 'Weed treatment', 'job'],
  fencing: ['Fence installation', 'Cattle guard service', 'job'],
  spill_response: ['Response crew', 'Remediation equipment', 'day'],
  norm_survey: ['Survey crew', 'Survey report', 'job'],
  h2s_safety: ['Safety attendant', 'Monitoring equipment', 'day'],
  leak_survey: ['OGI survey', 'Leak survey report', 'job'],
  site_reclamation: ['Site cleanup', 'Revegetation', 'job'],
  soil_sampling: ['Sample collection', 'Laboratory coordination', 'unit'],
  plug_abandon: ['P&A crew', 'P&A equipment', 'day'],
  surface_restoration: ['Surface grading', 'Revegetation', 'job'],
  cut_and_cap: ['Wellhead cut and cap', 'Site cleanup', 'job'],
  crane: ['Crane and operator', 'Rigging crew', 'hour'],
  forklift: ['Forklift rental', 'Telehandler rental', 'day'],
  light_tower: ['Light tower rental', 'Delivery and setup', 'day'],
  generator: ['Generator rental', 'Delivery and setup', 'day'],
  man_camp: ['Accommodation', 'Camp support services', 'day'],
  other: ['Service package', 'Additional service', 'job'],
};
export function starterServiceProducts(service: string): ServiceProduct[] {
  const porta = [['standard', 'Standard'], ['ada', 'ADA'], ['handwash', 'Hand-wash station'], ['holding', 'Holding tank']];
  if (service === 'porta_potty') return porta.map(([id, name]) => ({ id, name, unit: 'week', priceUsd: null }));
  if (RATE_ONLY_SERVICES.has(service)) return [];
  if (templates[service]) return templates[service].map(([id, name, unit]) => ({ id, name, unit, priceUsd: null }));
  const options = oilfieldOptions[service];
  if (options) return [{ id: 'service', name: options[0], unit: options[2], priceUsd: null }, { id: 'additional', name: options[1], unit: 'job', priceUsd: null }];
  return [{ id: 'service', name: contractorServiceLabel(service), unit: 'job', priceUsd: null }];
}
export function normalizeServiceCatalog(raw: unknown): ContractorServiceOffering[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  return raw.slice(0, CONTRACTOR_SERVICES.length).flatMap(value => {
    if (!value || typeof value !== 'object' || !ids.has(value.service) || seen.has(value.service)) return [];
    seen.add(value.service);
    const productIds = new Set<string>();
    const products: ServiceProduct[] = (Array.isArray(value.products) ? value.products : []).slice(0, 100).flatMap((p: any) => {
      if (!p || !/^[a-zA-Z0-9_-]{1,80}$/.test(p.id) || productIds.has(p.id) || !String(p.name || '').trim() || !SERVICE_PRICE_UNITS.includes(p.unit)) return [];
      productIds.add(p.id);
      const price = typeof p.priceUsd === 'number' && Number.isFinite(p.priceUsd) && p.priceUsd >= 0 && p.priceUsd <= 1000000 ? Math.round(p.priceUsd * 100) / 100 : null;
      return [{ id: p.id, name: String(p.name).trim().slice(0, 100), unit: p.unit, priceUsd: price }];
    });
    return [{ service: value.service, products }];
  });
}
export function serviceProducts(card: { serviceCatalog?: ContractorServiceOffering[] } | null | undefined, service: string) {
  return card?.serviceCatalog?.find(s => s.service === service)?.products || [];
}
export function serviceSelectionSummary(products: ServiceProduct[], selection: ServiceSelection[]) {
  return selection.map(line => {
    const product = products.find(p => p.id === line.productId);
    return product ? `${product.name}: ${line.quantity} ${product.unit}${line.quantity === 1 ? '' : 's'}` : '';
  }).filter(Boolean).join('\n');
}
export function serviceSelectionTotal(products: ServiceProduct[], selection: ServiceSelection[]): number | null {
  if (!selection.length) return null;
  let cents = 0;
  for (const line of selection) {
    const product = products.find(p => p.id === line.productId);
    if (!product || product.priceUsd == null || !Number.isFinite(line.quantity) || line.quantity <= 0 || line.quantity > 1000000) return null;
    cents += Math.round(product.priceUsd * 100 * line.quantity);
  }
  return cents;
}

/** Adapter to the existing WM calculator; its rental flow and day curve stay intact. */
export function publishedWasteRates(card: { serviceCatalog?: ContractorServiceOffering[] } | null | undefined) {
  const products = serviceProducts(card, 'waste_management');
  if (!products.length) return null;
  const weeks = products.filter(p => p.id.endsWith('_week') && p.priceUsd != null && p.priceUsd >= 50 && p.priceUsd <= 20000);
  return { weekPackageUsdBySize: Object.fromEntries(weeks.map(p => [p.id.replace('_week', ''), p.priceUsd!])),
    sizesOffered: weeks.map(p => p.id.replace('_week', '')),
    addlDayUsd: products.find(p => p.id === 'additional_day')?.priceUsd ?? 0,
    additionalDayPublished: products.some(p => p.id === 'additional_day' && p.priceUsd != null),
    defaultMode: 'package' as const, packageEnabled: true, dailyEnabled: false };
}
export function publishedProductPrice(card: { serviceCatalog?: ContractorServiceOffering[] } | null | undefined, service: string, idOrName: string) {
  const key = idOrName.trim().toLowerCase();
  const product = serviceProducts(card, service).find(p => p.id === key || p.name.toLowerCase() === key);
  return product?.priceUsd ?? null;
}

/** A backup's rate is comparable only for the same option and billing unit. */
export function matchingServiceProducts(offered: ServiceProduct[], requested: ServiceProduct[]) {
  return offered.filter(p => requested.some(r => r.id === p.id && r.unit === p.unit && r.name.trim().toLowerCase() === p.name.trim().toLowerCase()));
}
